import React, { useState, useEffect } from 'react';
import { setWaitlistOptedInCookie, setResultsPopupShownCookie } from '../utils/cookieManager';

interface FormErrors {
  email?: string;
  general?: string;
}

interface ResultsWaitlistPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

const ResultsWaitlistPopup: React.FC<ResultsWaitlistPopupProps> = ({ isVisible, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [buttonText, setButtonText] = useState('Join the Waitlist');

  // Kit form success detection
  useEffect(() => {
    if (!isVisible) return;

    // Load Kit script when popup opens
    const script = document.createElement('script');
    script.src = 'https://f.convertkit.com/ckjs/ck.5.js';
    script.async = true;
    document.head.appendChild(script);

    const handleKitSuccess = () => {
      setIsLoading(false);
      setButtonText('Success!');
      setWaitlistOptedInCookie();
      setResultsPopupShownCookie();
      setIsSuccess(true);
      
      setTimeout(() => {
        onClose();
      }, 2000);
    };

    // Set up success detection
    const setupSuccessDetection = () => {
      const form = document.querySelector('.results-seva-form');
      if (form) {
        form.addEventListener('submit', () => {
          setIsLoading(true);
          setButtonText('Joining...');
          
          // Check for success after form submission
          setTimeout(() => {
            if (window.location.href.includes(window.location.origin)) {
              handleKitSuccess();
            }
          }, 3000);
        });
      }
    };

    script.onload = () => {
      setTimeout(setupSuccessDetection, 1000);
    };

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setResultsPopupShownCookie();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Dismiss X button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {isSuccess ? (
          <div className="text-center pt-2">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Thanks for joining!</h3>
            <p className="text-gray-600">We'll notify you when the enhanced analyzer is ready.</p>
          </div>
        ) : (
          <div className="pt-2">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Want to save this report?</h3>
            
            <p className="text-gray-600 mb-6">
              Our upgraded analyzer lets you save, track changes, and access all your reports anytime.
            </p>

            {/* Kit Form */}
            <form 
              action="https://app.kit.com/forms/8644030/subscriptions" 
              className="results-seva-form formkit-form space-y-4" 
              method="post" 
              data-sv-form="8644030" 
              data-uid="eeab734572" 
              data-format="inline" 
              data-version="5"
            >
              {/* Hide Kit's default elements */}
              <style>
                {`
                  .formkit-alert { display: none !important; }
                  .formkit-powered-by-convertkit-container { display: none !important; }
                  .formkit-submit { display: none !important; }
                `}
              </style>
              
              <div>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="fields[first_name]"
                  placeholder="First name"
                  type="text"
                  disabled={isLoading}
                />
              </div>

              <div>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="email_address"
                  placeholder="Email address"
                  required
                  type="email"
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {errors.general && (
                <div className="mb-4 text-sm text-red-600">
                  {errors.general}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 font-medium"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {buttonText}
                  </div>
                ) : (
                  buttonText
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsWaitlistPopup;
